import React, { useState, useEffect } from 'react';
import { Shield, Clock, Key, Lock, Check, Loader2, AlertTriangle, X } from 'lucide-react';
import { useSecuritySettings, useUpdateSecuritySettings } from '../../hooks';
import { Input } from '../ui/input';
import { ToggleField } from '../ui/toggle';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';

export default function SecuritySettings() {
  const { data: settings, isLoading, error } = useSecuritySettings();
  const updateSettings = useUpdateSecuritySettings();

  const [formData, setFormData] = useState({
    session_timeout_minutes: 60,
    require_2fa: false,
    password_min_length: 12,
    password_require_uppercase: true,
    password_require_number: true,
    password_require_special: true,
    allowed_ip_ranges: [],
  });
  const [ipInput, setIpInput] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize from server data
  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
  };

  const handleAddIpRange = () => {
    if (ipInput && !formData.allowed_ip_ranges.includes(ipInput)) {
      setFormData((prev) => ({
        ...prev,
        allowed_ip_ranges: [...prev.allowed_ip_ranges, ipInput],
      }));
      setIpInput('');
      setSaveSuccess(false);
    }
  };

  const handleRemoveIpRange = (ip) => {
    setFormData((prev) => ({
      ...prev,
      allowed_ip_ranges: prev.allowed_ip_ranges.filter((r) => r !== ip),
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save security settings:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" className="h-5 w-40" />
        </div>
        <Skeleton variant="text" className="h-4 w-64" />
        <Skeleton variant="rectangular" className="h-[60px] w-full" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" className="h-[72px] w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center gap-3 py-10 text-orange-500">
        <AlertTriangle className="w-5 h-5" />
        <span>You need admin privileges to view security settings.</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-grey-900 dark:text-grey-50 m-0 flex items-center gap-2">
          <Shield className="w-5 h-5 text-teal-500" />
          Security Settings
        </h2>
        <p className="text-sm text-grey-500 dark:text-grey-400 mt-2">
          Configure security policies for your organization.
        </p>
      </div>

      {/* Admin Notice */}
      <div className="flex items-center gap-2 px-4 py-3 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg text-sm text-orange-600 dark:text-orange-400">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>Changes to security settings affect all users in your organization.</span>
      </div>

      {/* Session Settings */}
      <div className="space-y-4 pb-6 border-b border-grey-200 dark:border-grey-800">
        <div className="flex items-center gap-2 text-sm font-semibold text-grey-900 dark:text-grey-50">
          <Clock className="w-4 h-4 text-grey-500" />
          Session Settings
        </div>

        <Card variant="outline" className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-grey-900 dark:text-grey-50 mb-1">
                Session Timeout
              </div>
              <div className="text-xs text-grey-500">
                Automatically log out users after inactivity (5-480 minutes)
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Input
                type="number"
                className="w-24 text-center"
                value={formData.session_timeout_minutes}
                onChange={(e) => handleChange('session_timeout_minutes', parseInt(e.target.value) || 60)}
                min={5}
                max={480}
              />
              <span className="text-xs text-grey-500">minutes</span>
            </div>
          </div>
        </Card>

        <Card variant="outline" className="p-4">
          <ToggleField
            label="Require Two-Factor Authentication"
            hint="Require all users to set up 2FA for account access"
            checked={formData.require_2fa}
            onCheckedChange={(value) => handleChange('require_2fa', value)}
            disabled={updateSettings.isPending}
          />
        </Card>
      </div>

      {/* Password Requirements */}
      <div className="space-y-4 pb-6 border-b border-grey-200 dark:border-grey-800">
        <div className="flex items-center gap-2 text-sm font-semibold text-grey-900 dark:text-grey-50">
          <Key className="w-4 h-4 text-grey-500" />
          Password Requirements
        </div>

        <Card variant="outline" className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-grey-900 dark:text-grey-50 mb-1">
                Minimum Password Length
              </div>
              <div className="text-xs text-grey-500">
                Minimum number of characters required (8-128)
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Input
                type="number"
                className="w-24 text-center"
                value={formData.password_min_length}
                onChange={(e) => handleChange('password_min_length', parseInt(e.target.value) || 12)}
                min={8}
                max={128}
              />
              <span className="text-xs text-grey-500">characters</span>
            </div>
          </div>
        </Card>

        <Card variant="outline" className="p-4">
          <ToggleField
            label="Require Uppercase Letter"
            hint="Password must contain at least one uppercase letter"
            checked={formData.password_require_uppercase}
            onCheckedChange={(value) => handleChange('password_require_uppercase', value)}
            disabled={updateSettings.isPending}
          />
        </Card>

        <Card variant="outline" className="p-4">
          <ToggleField
            label="Require Number"
            hint="Password must contain at least one number"
            checked={formData.password_require_number}
            onCheckedChange={(value) => handleChange('password_require_number', value)}
            disabled={updateSettings.isPending}
          />
        </Card>

        <Card variant="outline" className="p-4">
          <ToggleField
            label="Require Special Character"
            hint="Password must contain at least one special character (!@#$%^&*)"
            checked={formData.password_require_special}
            onCheckedChange={(value) => handleChange('password_require_special', value)}
            disabled={updateSettings.isPending}
          />
        </Card>
      </div>

      {/* IP Allowlist */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-grey-900 dark:text-grey-50">
          <Lock className="w-4 h-4 text-grey-500" />
          IP Allowlist
        </div>
        <p className="text-xs text-grey-500">
          Restrict access to specific IP addresses or ranges. Leave empty to allow all IPs.
        </p>

        <div className="flex gap-2">
          <Input
            type="text"
            className="flex-1"
            value={ipInput}
            onChange={(e) => setIpInput(e.target.value)}
            placeholder="192.168.1.0/24 or 10.0.0.1"
            onKeyDown={(e) => e.key === 'Enter' && handleAddIpRange()}
          />
          <Button variant="secondary" onClick={handleAddIpRange}>
            Add
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {formData.allowed_ip_ranges.length === 0 ? (
            <span className="text-xs text-grey-500 italic">No IP restrictions configured</span>
          ) : (
            formData.allowed_ip_ranges.map((ip) => (
              <Badge
                key={ip}
                variant="secondary"
                className="gap-1.5 px-2.5 py-1 font-mono text-xs"
              >
                {ip}
                <button
                  onClick={() => handleRemoveIpRange(ip)}
                  className="hover:text-red-500 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))
          )}
        </div>
      </div>

      {/* Save Button */}
      <Button
        variant={saveSuccess ? 'success' : 'default'}
        size="lg"
        onClick={handleSave}
        disabled={updateSettings.isPending}
      >
        {updateSettings.isPending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : saveSuccess ? (
          <>
            <Check className="w-4 h-4" />
            Saved!
          </>
        ) : (
          'Save Security Settings'
        )}
      </Button>
    </div>
  );
}
